import { CSSResultGroup, html, LitElement, nothing, PropertyValues } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { style } from './image.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'

export const fitVariants = [ 'cover', 'contain', 'fill', 'none', 'scale-down']
export type fitVariantsTypes = typeof fitVariants

@customElement('cse-image')
export class CSEImage extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style, fontStyles]

  @property({ type: String })
  source = ''

  @property({ type: String })
  alt = ''

  @property({ type: String })
  loading: 'eager' | 'lazy' = 'lazy'

  @property({ type: Number })
  width: number | null = null

  @property({ type: Boolean, attribute: 'no-auto-size' })
  noAutoSize: boolean = false

  @property({ type: Boolean, attribute: 'gray-scale' })
  grayScale: boolean = false

  @property({ type: String })
  fit: fitVariantsTypes[number] = 'none'

  @property({ type: Number })
  height: number | null = null

  @property({ type: String, attribute: 'source-set' })
  sourceSet: string | null = null

  @property({ type: String, attribute: 'set-sizes' })
  setSizes: string | null = null

  @property({ type: String, attribute: 'title' })
  imageTitle: string | null = null

  @property({ type: String, attribute: 'fallback-source' })
  fallbackSource: string | null = null

  @property({ type: Boolean })
  border: boolean = false

  @property({ type: String })
  caption: string = ''

  validateProperties() {
    if (this.source.trim() === '') {
      console.warn('cse-image source is not provided', this)
    }

    if (this.alt.trim() === '') {
      console.warn('cse-image alternative text is not provided', this)
    }
  }

  _onerror() {
    if (this.fallbackSource !== null) {
      this.source = this.fallbackSource
    }
  }

  protected override firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties)

    this.validateProperties()
  }

  override render() {
    let autoSize = false
    if (!this.noAutoSize) {
      autoSize = this.height == null && this.width == null
    }

    return html`
      <img
        class="${classMap({
          'cse-image': true,
          'auto-size': autoSize,
          'font-body-s': true,
          [`fit-${this.fit}`]: true,
          'grayscale': this.grayScale,
          'border': this.border,
        })}"
        src="${this.source}"
        alt="${this.alt}"
        loading="${this.loading}"
        title="${this.imageTitle ?? nothing}"
        width="${this.width ?? nothing}"
        height="${this.height ?? nothing}"
        srcset="${this.sourceSet ?? nothing}"
        sizes="${this.setSizes ?? nothing}"
        @error="${this._onerror}"
      />

      ${this.caption
        ? html`<cse-text
            variant="caption-m"
            class="caption"
            style="${this.width ? `max-width: ${this.width}px;` : nothing}"
          >${this.caption}</cse-text>`
        : nothing
      }
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-image': CSEImage
  }
}
